import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Calendar, ClipboardList, Award } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { StatCards, UpcomingMatches, RecentRequests, UpcomingRoundRequests } from '@/components/DashboardWidgets';
import { ThinDonutCard } from '@/components/ThinDonutCard';
import { DashboardCustomizeBar, DashboardCustomizePanel, DashboardGrid } from '@/components/DashboardLayout';
import { useDashboardCustomization } from '@/hooks/useDashboardCustomization';
import { WIDGET_PERM } from '@/lib/dashboardPerms';
import { SystemOverviewCard } from '@/components/SystemOverviewWidgets';

const STATUS_BAR_COLORS = {
  submitted: '#3B82F6', under_review: '#F3CF5B', approved: '#10B981',
  partially_approved: '#7EC9C9', rejected: '#E44E50', cancelled: '#6B7280',
  cancellation_requested: '#F59E0B', pending_sponsor_approval: '#6763A9', delivered: '#7EC9C9',
  pending: '#F3CF5B', other: '#6B7280',
};

const defaultVis = { pieChart: true, systemOverview: true, upcomingMatches: true, upcomingRoundRequests: true, recentRequests: true };
const defaultOrder = ['pieChart', 'systemOverview', 'recentRequests', 'upcomingRoundRequests', 'upcomingMatches'];
const defaultSizes = { pieChart: 'small', systemOverview: 'small', recentRequests: 'small', upcomingRoundRequests: 'full', upcomingMatches: 'full' };

export default function ManagerDashboard() {
  const { t } = useTranslation();
  const { user, hasPerm } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [upcomingRound, setUpcomingRound] = useState(null);

  const customize = useDashboardCustomization('isp_mgr_dashboard', defaultVis, defaultOrder, defaultSizes, (k) => hasPerm(WIDGET_PERM[k]));

  useEffect(() => {
    api.get('/api/dashboard/stats').then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/api/dashboard/system-overview').then(r => setOverview(r.data)).catch(() => {});
    api.get('/api/dashboard/upcoming-round').then(r => setUpcomingRound(r.data)).catch(() => {});
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t('loading')}</div>;

  const simpleCards = [
    { label: t('my_sponsors'), value: stats?.total_sponsors || 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50', link: '/sponsors' },
    { label: t('active_rights'), value: stats?.active_rights || 0, icon: Award, color: 'text-indigo-600', bg: 'bg-indigo-50', link: '/rights' },
    { label: t('upcoming_matches'), value: stats?.upcoming_matches || 0, icon: Calendar, color: 'text-purple-600', bg: 'bg-purple-50', link: '/matches' },
  ];
  const requestCard = {
    label: t('total_requests'), value: stats?.total_requests || 0, icon: ClipboardList, color: 'text-gray-700', bg: 'bg-gray-100', link: '/requests',
    breakdown: [
      { label: t('pending'), value: stats?.pending_requests || 0, dotColor: 'bg-amber-500' },
      { label: t('approved'), value: stats?.approved_requests || 0, dotColor: 'bg-green-500' },
      { label: t('rejected'), value: stats?.rejected_requests || 0, dotColor: 'bg-red-500' },
    ]
  };
  const pieData = Object.entries(stats?.request_status_breakdown || {})
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({
      name: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      value,
      color: STATUS_BAR_COLORS[key],
    }));

  const widgetList = [
    { key: 'pieChart', label: t('requests_overview') },
    { key: 'systemOverview', label: 'System Overview' },
    { key: 'upcomingMatches', label: t('upcoming_matches') || 'Upcoming Matches' },
    { key: 'upcomingRoundRequests', label: 'Upcoming Round Requests' },
    { key: 'recentRequests', label: t('recent_requests') },
  ];
  const allowedWidgetList = widgetList.filter(w => hasPerm(WIDGET_PERM[w.key]));

  const renderWidget = (key, size) => {
    switch (key) {
      case 'pieChart': return <ThinDonutCard title={t('requests_overview')} data={pieData} />;
      case 'upcomingMatches': return <UpcomingMatches thisRoundMatches={stats?.this_round_matches} thisRound={stats?.this_round} nextRoundMatches={stats?.next_round_matches} nextRound={stats?.next_round} size={customize.widgetSizes['upcomingMatches'] || 'small'} />;
      case 'recentRequests': return <RecentRequests requests={stats?.recent_requests} size={customize.widgetSizes['recentRequests'] || 'full'} />;
      case 'upcomingRoundRequests': return <UpcomingRoundRequests data={upcomingRound} />;
      case 'systemOverview': return <SystemOverviewCard data={overview} />;
      default: return null;
    }
  };

  return (
    <div data-testid="manager-dashboard" className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:page-title">{t('welcome_name', { name: user?.name?.split(' ')[0] || '' })}</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-xs lg:text-sm hidden sm:block">{t('your_activity')}</p>
        </div>
        <DashboardCustomizeBar {...customize} widgetList={allowedWidgetList} />
      </div>
      <DashboardCustomizePanel {...customize} widgetList={allowedWidgetList} />
      {hasPerm(WIDGET_PERM.statCards) && <StatCards cards={simpleCards} requestCard={requestCard} />}
      <DashboardGrid {...customize} renderWidget={renderWidget} />
    </div>
  );
}
